// src/components/AmenityCard.tsx

import React from "react";
import { Card, CardContent, CardMedia, Typography, Button, CardActions, CardHeader, Dialog, Paper, Box, TextField, Stack } from "@mui/material";
import DatePickerComponent from "../CommonComponents/CustomDatePicker";
import dayjs from "dayjs";
import DropdownSelection from "../CommonComponents/DropdownSelection";
import { Priority, ToDo } from "../../Types/UnitFeatureTypes";
import { Task } from "@mui/icons-material";

interface TaskDialogProps {
    open: boolean,
    displayName: string,
    onCloseFunc: () => void,
    actionOne: {label: string, color: string, func: () => void },
    actionTwo: {label: string, color: string, func: () => void },
    formData: ToDo,
    setFormData: (data: any) => void,
    errors: { [key: string]: string }
}

const TaskDialog: React.FC<TaskDialogProps> = ({
    open,
    displayName,
    onCloseFunc,
    actionOne,
    actionTwo,
    formData,
    setFormData,
    errors = {}
}) => {
    return (
        <Dialog sx={{mt:5}} open={open} onClose={onCloseFunc}>
        <Paper elevation={5} sx={{width: 400, padding: 3, borderRadius: 3}}>
            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 2}}>
                <Task sx={{mr:1}} />
                <Typography variant="h5">{displayName}</Typography>
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', mt: 2}}>
                <TextField
                    label="Title" 
                    defaultValue={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    variant="outlined" 
                    disabled={displayName === "View Task"}
                    sx={{mb: 2}}
                    error={!!errors.title}
                    helperText={errors.title}
                />
                <TextField 
                    label="Description" 
                    defaultValue={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    variant="outlined" 
                    multiline
                    disabled={displayName === "View Task"}
                    rows={4}
                    sx={{mb: 2}}
                    error={!!errors.description}
                    helperText={errors.description}
                />
                <DatePickerComponent
                    labelValue="Due Date"
                    selectedDate={dayjs(formData.due_date)}
                    setSelectedDate={(newDate) => setFormData({ ...formData, due_date: newDate!.toDate() })}
                    minDate={dayjs()}
                    isReadOnly={displayName === "View Task"}
                />
                <Stack spacing={2} direction="row">
                    <DropdownSelection
                        isReadOnly={displayName === "View Task"}
                        dropDownLabel='Hour'
                        showHeader={false}
                        sxObject={{ mb: 2, mt: 2}}
                        labelSx={{mt:2}}
                        currentValue={new Date(formData.due_date).getHours().toString()}
                        selectionValues={
                            Array.from({length: 24}, (_, i) => i).map((hour) => ({
                                value: hour.toString(),
                                label: hour.toString(),
                                color: 'default',
                            }))
                        }
                        variant='outlined'
                        handleOnChange={(e) => setFormData({ ...formData, due_date: new Date(formData.due_date.setHours(parseInt(e.target.value))) })}
                    />
                    <DropdownSelection
                        dropDownLabel='Minute'
                        isReadOnly={displayName === "View Task"}
                        showHeader={false}
                        sxObject={{ mb: 2, mt: 2}}
                        labelSx={{mt:2}}
                        currentValue={new Date(formData.due_date).getMinutes().toString()}
                        selectionValues={
                            Array.from({length: 60}, (_, i) => i).map((minute) => ({
                                value: minute.toString(),
                                label: minute.toString(),
                                color: 'default',
                            }))
                        }
                        variant='outlined'
                        handleOnChange={(e) => setFormData({ ...formData, due_date: new Date(formData.due_date.setMinutes(parseInt(e.target.value))) })}
                    />
                </Stack>
                <DropdownSelection
                    dropDownLabel='Priority'
                    isReadOnly={displayName === "View Task"}
                    showHeader={false}
                    sxObject={{ mb: 2, mt: 2}}
                    labelSx={{mt:2}}
                    currentValue={formData.priority}
                    selectionValues={Object.values(Priority).map((priority) => ({
                        value: priority,
                        label: priority,
                        color: 'default',
                    }))}
                    variant='outlined'
                    handleOnChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                />
                <Stack spacing={2} direction="row">
                    {actionOne.label && <Button onClick={actionOne.func} fullWidth variant="contained" sx={{backgroundColor: actionOne.color}}>{actionOne.label}</Button>}
                    <Button onClick={actionTwo.func} fullWidth variant="contained" sx={{backgroundColor: actionTwo.color}}>{actionTwo.label}</Button>
                </Stack>
            </Box>
        </Paper>
        </Dialog>
    );
};

export default TaskDialog;
